import { useEffect, useState, useContext } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Spin } from "antd";
import clsx from "clsx";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useParams } from "react-router-dom";
import { journalsQueries } from "@/entities/journal";
import {
  JournalsResponsibleTable,
  JournalsSubscribersTable,
} from "@/entities/journal/ui";
import { AiChat } from "@/features/ai-chat";
import {
  useAmendmentsStore,
  useJournalVersionsStore,
} from "@/features/journal-version-list";
import { ScrollToTopButton } from "@/features/scroll-to-top-button";
import {
  EntityBaseAbilityBuilder,
  useUpdateAbility,
  AbilityContext,
} from "@/shared/lib/casl";
import { useFlags } from "@/shared/lib/flags";
import {
  useCoordinator,
  useDisclosure,
  useNavigationPosition,
} from "@shared/lib/hooks";
import { useAppRoutes, useNavigate } from "@shared/lib/react-router";
import {
  JournalsInfoTableNew,
  DeveloperCompanyTableNew,
  TecCustomerCompanyTableNew,
  BuildingContractorCompanyTableNew,
  OtherDevelopersTableNew,
  DocumentationPermissionTableNew,
  DocumentationExaminationTableNew,
  DocumentationContractorCompanyTableNew,
  DesignerSupervisionsTableNew,
} from "../../../..";
import { JournalsSideTable } from "../../../../journals-side-table";
import { SideButtonList } from "../../../../journals-side-table/ui";
import { TitleChangeReasonForm } from "../../../../title-change-reason-form";
import { JournalsAmendmentsRequisitesDiff } from "../../amendments/ui";
import { getDefaultValue, useResetterTitle } from "../model";
import { formSchema } from "../types/zod-validation";
import { TitleRejectedBanner } from "./title-rejected-banner";
import "./title-general-tab.scss";

interface ITitleGeneralTab {
  isLoading?: boolean;
}

export const TitleGeneralTab = ({ isLoading }: ITitleGeneralTab) => {
  const { titleId, projectId, objectId, mainJournalId } = useCoordinator();
  const ability = useContext(AbilityContext);
  const { isTitleAmendmentsForm, isTitleAmendmentsNew, isTitleAmendmentsView } =
    useNavigationPosition();
  const isAmendmentsEnabled = useFlags("ojrFormationFlowEnabledV1");
  const appRoutes = useAppRoutes();
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId?: string }>();
  const amendmentRequests = useAmendmentsStore((s) => s.requests);
  const createDraft = useAmendmentsStore((s) => s.createDraft);
  const journalVersions = useJournalVersionsStore((s) => s.versions);
  const currentAmendment = requestId
    ? (amendmentRequests.find((r) => r.key === requestId) ?? null)
    : null;
  const isSystemAmendment = currentAmendment?.requestSource === "SYSTEM";
  const isAmendmentReadOnly =
    isTitleAmendmentsForm &&
    (currentAmendment?.status !== "NEW" ||
      currentAmendment.requestSource === "SYSTEM");

  const { isOpen } = useDisclosure({ isOpen: true });
  const { isOpen: isChatOpen, onToggle: onToggleChat } = useDisclosure({
    isOpen: true,
  });

  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  useUpdateAbility({
    // @ts-expect-error permissions из swagger не совпадает по типам
    permissions: data?.mainJournal?.titleJournal?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });

  const isViewMode = !ability.can("edit", "title_journals");

  const defaultValues = getDefaultValue(data);

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const {
    reset,
    formState: { errors },
  } = methods;
  const { control } = methods;

  useResetterTitle({ data, reset });

  const [developerTypeLocal, setDeveloperTypeLocal] = useState<string>(
    data?.mainJournal?.titleJournal?.developer?.developerType || "developer",
  );

  useEffect(() => {
    setDeveloperTypeLocal(
      data?.mainJournal?.titleJournal?.developer?.developerType || "developer",
    );
  }, [data]);

  useEffect(() => {
    if (!isAmendmentsEnabled || !isTitleAmendmentsNew) {
      return;
    }

    const actualVersion = journalVersions.find(
      (version) => version.status === "ACTUAL",
    );
    const draft = createDraft(
      titleId,
      "Текущий пользователь",
      actualVersion?.versionNumber,
    );
    navigate(
      appRoutes.journals.amendmentsView(
        projectId,
        objectId,
        mainJournalId,
        titleId,
        draft.key,
      ),
      { replace: true },
    );
  }, [
    appRoutes.journals,
    createDraft,
    isAmendmentsEnabled,
    isTitleAmendmentsNew,
    journalVersions,
    mainJournalId,
    navigate,
    objectId,
    projectId,
    titleId,
  ]);

  if (isTitleAmendmentsForm && !isAmendmentsEnabled) {
    return (
      <Navigate
        to={appRoutes.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat("general")}
        replace
      />
    );
  }

  if (isTitleAmendmentsNew) {
    return <Spin spinning size="large" />;
  }

  if (isTitleAmendmentsView && !currentAmendment) {
    return (
      <Navigate
        to={appRoutes.journals.amendments(
          projectId,
          objectId,
          mainJournalId,
          titleId,
        )}
        replace
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <TitleRejectedBanner status={data?.mainJournal?.titleJournal?.status} />
      <Spin spinning={isLoading} size="large">
        {!isTitleAmendmentsForm && (
          <JournalsResponsibleTable titleId={titleId} type="title" />
        )}
        {!isTitleAmendmentsForm && (
          <JournalsSubscribersTable titleId={titleId} type="title" />
        )}
        <div
          className={clsx(
            "journals-columns-wrapper",
            isTitleAmendmentsForm && "journals-columns-wrapper--amendment",
            isSystemAmendment && "journals-columns-wrapper--amendment-system",
          )}
        >
          <div className="journals-main-column-wrapper">
            <>
              {isTitleAmendmentsForm ? (
                <>
                  {currentAmendment?.requestSource === "SYSTEM" &&
                  currentAmendment.changesList ? (
                    <JournalsAmendmentsRequisitesDiff
                      changes={currentAmendment.changesList}
                    />
                  ) : null}
                  <TitleChangeReasonForm request={currentAmendment} />
                </>
              ) : null}
              {!isTitleAmendmentsForm || !isSystemAmendment ? (
                <fieldset
                  className="title-general-tab__amendment-sections"
                  disabled={isAmendmentReadOnly}
                >
                  <JournalsInfoTableNew errors={errors} />
                  <DeveloperCompanyTableNew
                    developerTypeLocal={developerTypeLocal}
                    setDeveloperTypeLocal={setDeveloperTypeLocal}
                  />
                  <TecCustomerCompanyTableNew />
                  <DocumentationPermissionTableNew isViewMode={isViewMode} />
                  <DocumentationContractorCompanyTableNew />
                  <DesignerSupervisionsTableNew />
                  <DocumentationExaminationTableNew isViewMode={isViewMode} />
                  <BuildingContractorCompanyTableNew />
                  <OtherDevelopersTableNew />
                </fieldset>
              ) : null}
              {isSystemAmendment ? (
                <div className="title-general-tab__amendment-actions">
                  <SideButtonList control={control} />
                </div>
              ) : null}
            </>
          </div>
          {isSystemAmendment ? null : (
            <div className="journals-side-column-wrapper">
              <div
                className={clsx(
                  "journals-side-sticky-container",
                  isOpen && "journals-side-sticky-container--open",
                )}
              >
                <JournalsSideTable control={control} />
                {!isTitleAmendmentsForm ? (
                  <AiChat isOpen={isChatOpen} onToggle={onToggleChat} />
                ) : null}
                <ScrollToTopButton>Наверх</ScrollToTopButton>
              </div>
            </div>
          )}
        </div>
      </Spin>
    </FormProvider>
  );
};
